import { useEffect, useMemo, useState } from "react";
import OrderArticle, {
  initOrderArticle,
  OrderArticleAnalytics,
  OrderArticleOrigin,
} from "../../../global/utils/models/order/OrderArticle";
import { useAppSelector } from "../../../global/utils/redux/store";
import Article from "../../../global/utils/models/menu/Article";
import OrderArticleSelectorPageContent from "./OrderArticleSelectorPageContent";
import Articlegroup from "../../../global/utils/models/menu/Articlegroup";
import { ArticleTranslationsContext } from "../../../global/utils/translations/useOptionTranslations";
import { selectArticlesMap } from "../../../global/utils/redux/selectors/selectArticlesMap";
import { OrderArticleContext } from "../../../global/utils/contexts/OrderArticleContext";

interface Props {
  upsellDetails?: Pick<OrderArticleAnalytics, "upsellType" | "upsellSource" | "upsellId">;
  orderArticle?: OrderArticle;
  article?: Article;
  articlegroup?: Articlegroup;
  isUpselled?: boolean;
  isPiggyReward?: boolean;
  callAfterAdding?: () => void;
}

function OrderArticleSelectorPage(props: Props) {
  const [orderArticle, setOrderArticle] = useState<OrderArticle | null>(null);

  const articlesMap = useAppSelector(selectArticlesMap);

  const article = useMemo<Article | null>(() => {
    if (props.orderArticle) {
      return { ...props.orderArticle.article, articlegroup: props.articlegroup };
    } else if (props.article) {
      return { ...props.article, articlegroup: props.articlegroup };
    }
    return null;
  }, [props.article, props.articlegroup, props.orderArticle]);

  const defaultOrderArticle = useMemo<OrderArticle | null>(() => {
    return props.orderArticle
      ? { ...props.orderArticle, articlegroup: props.articlegroup }
      : article
        ? initOrderArticle(articlesMap, article, 1, {
            editable: !props.isPiggyReward,
            isUpselled: props.isUpselled ?? false,
            addedOrigin: props.isPiggyReward ? OrderArticleOrigin.PIGGY : OrderArticleOrigin.MENU,

            articlegroup: props.articlegroup,
            position: props.article?.sortKey,
            ...props.upsellDetails,
          })
        : null;
  }, [
    props.orderArticle,
    props.articlegroup,
    props.isPiggyReward,
    props.isUpselled,
    props.article?.sortKey,
    props.upsellDetails,
    article,
    articlesMap,
  ]);

  useEffect(() => {
    setOrderArticle(defaultOrderArticle);
  }, [article, props.orderArticle]); /// do NOT add defaultOrderArticle as dependency

  if (!article || !orderArticle) {
    return null;
  } else {
    return (
      <OrderArticleContext.Provider value={{ orderArticle, setOrderArticle }}>
        <ArticleTranslationsContext.Provider value={orderArticle.article.translations}>
          <OrderArticleSelectorPageContent
            orderArticle={orderArticle}
            articlegroup={props.articlegroup}
            modifyingOrderArticle={props.orderArticle}
            isPiggyReward={props.isPiggyReward ?? false}
            callAfterAdding={props.callAfterAdding}
          />
        </ArticleTranslationsContext.Provider>
      </OrderArticleContext.Provider>
    );
  }
}

export default OrderArticleSelectorPage;
